import React, { useEffect, useLayoutEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { Form, FormGroup } from 'reactstrap';
import { change, Field, reduxForm, formValueSelector } from 'redux-form';
import { tzNormalizeDate } from '../../../../utils/date';
import { DATE_TIME_FORMAT, INT_DATE_FORMAT } from '../../../../config/locale';
import FormItem from '../../../common/forms/FormItem';
import Table from '../../../common/Table';
import { ITINERARY_SUPPLIER_FOOD_SERVICES_COLUMNS } from '../../../../config/columns';
import { filterMethodCaseInsensitive } from '../../../../utils/filter-methods';
import {
  DEFAULT_PAGE_SIZE,
  FOOD_PUBLIC_TYPE,
} from '../../../../config/constants';
import Loader from '../../../common/Loader';
import FormFooter from '../../../common/forms/FormFooter';
import ItinerarySupplierFoodSearchForm from './ItinerarySupplierFoodSearchForm';
import {
  clearItinerarySupplierFoodService,
  clearItinerarySupplierFoodServiceDetail,
  getItinerarySupplierFoodAssignmentService,
  getItinerarySupplierFoodAssignmentServiceDetail,
} from '../../../../actions/food/ItinerarySupplierFood';
import {
  clearSupplierFoodType,
  getSupplierFoodTypeBySupplierAndItinerary,
} from '../../../../actions';
import DynamicForm from '../../../common/forms/DynamicForm';
import { ITINERARY_SUPPLIER_FOOD_TYPE_COLUMNS } from '../../../../config/dynamicFormFields';
import CabinCrewSelect from '../../../common/forms/select/CabinCrewSelect';
import TextInput from '../../../common/forms/input/TextInput';

const FORM_NAME = 'ItinerarySupplierFoodForm';

const ItinerarySupplierFoodForm = ({
  handleSubmit,
  onSubmit,
  dispatchGetItinerarySupplierFoodAssignmentService,
  dispatchClearItinerarySupplierFoodService,
  dispatchClearItinerarySupplierFoodServiceDetail,
  dispatchGetItinerarySupplierFoodAssignmentServiceDetail,
  dispatchClearSupplierFoodType,
  dispatchGetSupplierFoodTypeBySupplierAndItinerary,
  itinerarySupplierFoodServices,
  itinerarySupplierFoodServiceDetail,
  loadingItinerarySupplierFoodServiceDetail,
  loadingItinerarySupplierFoodServices,
  loadingSupplierFoodType,
  supplierFoodType,
  supplierFoodTypeList,
  dispatchChange,
  loading,
}) => {
  const [selectedItinerary, setSelectedItinerary] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  useLayoutEffect(
    () => () => {
      dispatchClearItinerarySupplierFoodService();
      dispatchClearItinerarySupplierFoodServiceDetail();
      dispatchClearSupplierFoodType();
      dispatchChange(FORM_NAME, 'supplierFoodTypeList', []);
    },
    [
      dispatchClearItinerarySupplierFoodService,
      dispatchClearItinerarySupplierFoodServiceDetail,
      dispatchClearSupplierFoodType,
      dispatchChange,
    ],
  );

  useEffect(() => {
    if (!supplierFoodType) {
      dispatchChange(FORM_NAME, 'supplierFoodTypeList', []);
      return;
    }

    const foodTypeList = supplierFoodType.toJS
      ? supplierFoodType.toJS()
      : supplierFoodType;

    if (!foodTypeList || foodTypeList.length === 0) {
      dispatchChange(FORM_NAME, 'supplierFoodTypeList', []);
      return;
    }

    const formFoodTypeList = foodTypeList.map(
      ({ food, foodId, cost, publicType }) => ({
        food:
          food && food.id
            ? {
                value: food.id,
                label: food.name,
              }
            : null,

        foodId: food && food.id ? food.id : foodId,
        publicType: FOOD_PUBLIC_TYPE[publicType] || null,
        quantity: null,
        unitCost: cost || 0,
        cost: null,
      }),
    );

    dispatchChange(FORM_NAME, 'supplierFoodTypeList', formFoodTypeList);
  }, [supplierFoodType, dispatchChange]);

  useEffect(() => {
    if (!supplierFoodTypeList || supplierFoodTypeList.length === 0) return;

    supplierFoodTypeList.forEach(({ quantity, unitCost = 0, cost }, index) => {
      const parsedQuantity = Number(quantity);
      const parsedUnitCost = Number(unitCost);

      const isNumeric =
        quantity !== null &&
        quantity !== undefined &&
        quantity !== '' &&
        !Number.isNaN(parsedQuantity);

      const newCost = isNumeric ? parsedQuantity * parsedUnitCost : null;

      if (cost !== newCost) {
        dispatchChange(
          FORM_NAME,
          `supplierFoodTypeList[${index}].cost`,
          newCost,
        );
      }
    });
  }, [supplierFoodTypeList, dispatchChange]);

  const onHandleSubmit = (formValues) => {
    dispatchClearItinerarySupplierFoodServiceDetail();
    dispatchClearSupplierFoodType();
    dispatchChange(FORM_NAME, 'supplierFoodTypeList', []);

    const supplierId = formValues.supplierId || {};
    const supplierIdValue = supplierId.value || supplierId;

    setSelectedItinerary(null);
    setSelectedSupplier(supplierIdValue || null);

    dispatchGetItinerarySupplierFoodAssignmentService({
      supplierId: supplierIdValue,
      departureDate: tzNormalizeDate({
        date: formValues.dateString,
        format: INT_DATE_FORMAT,
      }),
    });
  };

  const onSelectItinerary = (itinerary) => {
    dispatchClearItinerarySupplierFoodServiceDetail();
    dispatchClearSupplierFoodType();
    dispatchChange(FORM_NAME, 'supplierFoodTypeList', []);

    setSelectedItinerary(itinerary);

    dispatchGetItinerarySupplierFoodAssignmentServiceDetail({
      itineraryId: itinerary.itineraryId,
    });

    if (selectedSupplier && itinerary && itinerary.itineraryId) {
      dispatchGetSupplierFoodTypeBySupplierAndItinerary({
        supplierId: selectedSupplier,
        itineraryId: itinerary.itineraryId,
      });
    }
  };

  const hasData = itinerarySupplierFoodServices.size > 0;

  const detail =
    itinerarySupplierFoodServiceDetail &&
    itinerarySupplierFoodServiceDetail.toJS
      ? itinerarySupplierFoodServiceDetail.toJS()
      : null;

  const onHandleSubmitLiquidation = async (formValues) => {
    const saved = await onSubmit(
      formValues,
      selectedItinerary,
      selectedSupplier,
      detail ? detail.registeredBus : null,
      detail ? detail.passengerFlowForItinerary : null,
    );

    if (!saved) {
      return;
    }

    setSelectedItinerary(null);

    dispatchClearItinerarySupplierFoodServiceDetail();

    dispatchClearSupplierFoodType();

    dispatchChange(FORM_NAME, 'supplierFoodTypeList', []);

    dispatchChange(FORM_NAME, 'cabinCrewId', null);

    dispatchChange(FORM_NAME, 'comment', null);
  };
  return (
    <>
      <h3>Búsqueda de Viajes por Proveedor</h3>

      <ItinerarySupplierFoodSearchForm
        onSubmit={onHandleSubmit}
        initialValues={{
          dateString: tzNormalizeDate(),
        }}
      />

      <hr />

      {loadingItinerarySupplierFoodServices && <Loader />}

      {!loadingItinerarySupplierFoodServices && hasData && (
        <Table
          manual={false}
          columns={ITINERARY_SUPPLIER_FOOD_SERVICES_COLUMNS}
          data={itinerarySupplierFoodServices.toJS()}
          defaultPageSize={DEFAULT_PAGE_SIZE}
          loading={loadingItinerarySupplierFoodServices}
          highlightSelectedRow
          defaultFilterMethod={filterMethodCaseInsensitive}
          getRowData={onSelectItinerary}
          showPagination
          filterable
          keyField="itineraryId"
        />
      )}

      {!loadingItinerarySupplierFoodServices && !hasData && (
        <div className="alert alert-info">
          Para ver datos utilice los filtros
        </div>
      )}

      {!loadingItinerarySupplierFoodServiceDetail &&
        itinerarySupplierFoodServiceDetail &&
        itinerarySupplierFoodServiceDetail.isEmpty() && (
          <div className="alert alert-info">
            Seleccione un viaje para ver su detalle y proceder a crear la
            alimentación de itinerario
          </div>
        )}

      {loadingItinerarySupplierFoodServiceDetail && <Loader />}

      {!loadingItinerarySupplierFoodServiceDetail &&
        itinerarySupplierFoodServiceDetail &&
        !itinerarySupplierFoodServiceDetail.isEmpty() && (
          <>
            <h3 className="mt-4">Información del itinerario</h3>

            <div className="alert alert-secondary">
              <div>
                <strong>Bus:</strong>{' '}
                {detail.registeredBus && detail.registeredBus.companyBusId
                  ? detail.registeredBus.companyBusId
                  : '-'}
              </div>

              <div>
                <strong>Origen:</strong> {detail.originLocationName || '-'}
              </div>

              <div>
                <strong>Destino:</strong>{' '}
                {detail.destinationLocationName || '-'}
              </div>

              <div>
                <strong>Fecha salida:</strong>{' '}
                {detail.departureDate
                  ? tzNormalizeDate({
                      date: detail.departureDate,
                      format: DATE_TIME_FORMAT,
                    })
                  : '-'}
              </div>
            </div>

            <h4>Conductores</h4>

            {detail.driverList && detail.driverList.length > 0 ? (
              <div className="mb-3">
                <Table
                  columns={[
                    {
                      Header: 'Nombre',
                      accessor: 'driverName',
                      className: 'text-center',
                    },
                    {
                      Header: 'DNI',
                      accessor: 'driverIdDocumentNumber',
                      className: 'text-center',
                    },
                  ]}
                  data={detail.driverList}
                  defaultPageSize={detail.driverList.length}
                  showPagination={false}
                />
              </div>
            ) : (
              <div className="alert alert-info">Sin conductores</div>
            )}

            <h4>Servicio a Bordo</h4>

            {detail.cabinCrewList && detail.cabinCrewList.length > 0 ? (
              <div className="mb-3">
                <Table
                  columns={[
                    {
                      Header: 'Nombre',
                      accessor: 'cabinCrewName',
                      className: 'text-center',
                    },
                    {
                      Header: 'DNI',
                      accessor: 'cabinCrewIdDocumentNumber',
                      className: 'text-center',
                    },
                  ]}
                  data={detail.cabinCrewList}
                  defaultPageSize={detail.cabinCrewList.length}
                  showPagination={false}
                />
              </div>
            ) : (
              <div className="alert alert-info">Sin Servicio a bordo</div>
            )}

            <h4>Flujo de pasajeros</h4>

            {detail.passengerFlowForItinerary &&
            detail.passengerFlowForItinerary.passengerFlowForLocationList &&
            detail.passengerFlowForItinerary.passengerFlowForLocationList
              .length > 0 ? (
              <div className="mb-3">
                <Table
                  columns={[
                    {
                      Header: 'Parada Autorizada',
                      accessor: 'locationName',
                      className: 'text-center',
                    },
                    {
                      Header: 'Embarques',
                      accessor: 'boardingPassengers',
                      className: 'text-center',
                    },
                    {
                      Header: 'Desembarques',
                      accessor: 'leavingPassengers',
                      className: 'text-center',
                    },
                  ]}
                  data={
                    detail.passengerFlowForItinerary
                      .passengerFlowForLocationList || []
                  }
                  defaultPageSize={
                    detail.passengerFlowForItinerary
                      .passengerFlowForLocationList
                      ? detail.passengerFlowForItinerary
                          .passengerFlowForLocationList.length
                      : DEFAULT_PAGE_SIZE
                  }
                  showPagination={false}
                />
              </div>
            ) : (
              <div className="alert alert-info">Sin flujo</div>
            )}
          </>
        )}

      <Form className="mt-3" onSubmit={handleSubmit(onHandleSubmitLiquidation)}>
        {loadingSupplierFoodType && <Loader />}

        {!loadingSupplierFoodType &&
          supplierFoodType &&
          !supplierFoodType.isEmpty() && (
            <DynamicForm
              name="supplierFoodTypeList"
              columns={ITINERARY_SUPPLIER_FOOD_TYPE_COLUMNS}
              showAddButton={false}
              showRemoveButton={false}
            />
          )}

        {!loadingSupplierFoodType &&
          selectedItinerary &&
          supplierFoodType &&
          supplierFoodType.isEmpty() && (
            <div className="alert alert-info">
              El proveedor no tiene alimentos configurados para este itinerario
            </div>
          )}

        <FormGroup row>
          <FormItem label="Tripulante">
            <Field name="cabinCrewId" component={CabinCrewSelect} />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Comentarios">
            <Field
              name="comment"
              component={TextInput}
              type="textarea"
              placeholder="Comentarios"
            />
          </FormItem>
        </FormGroup>

        <FormFooter
          saveButtonDisabled={loading || !selectedItinerary}
          saveButtonIcon={loading ? 'fa fa-spinner fa-spin' : 'fa fa-save'}
        />
      </Form>
    </>
  );
};

ItinerarySupplierFoodForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  dispatchGetItinerarySupplierFoodAssignmentService: PropTypes.func.isRequired,
  dispatchClearItinerarySupplierFoodService: PropTypes.func.isRequired,
  dispatchClearItinerarySupplierFoodServiceDetail: PropTypes.func.isRequired,
  dispatchGetItinerarySupplierFoodAssignmentServiceDetail:
    PropTypes.func.isRequired,
  dispatchClearSupplierFoodType: PropTypes.func.isRequired,
  dispatchGetSupplierFoodTypeBySupplierAndItinerary: PropTypes.func.isRequired,
  itinerarySupplierFoodServices: PropTypes.instanceOf(Immutable.List),
  loadingItinerarySupplierFoodServices: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  itinerarySupplierFoodServiceDetail: PropTypes.instanceOf(Immutable.Map),
  loadingItinerarySupplierFoodServiceDetail: PropTypes.bool.isRequired,
  loadingSupplierFoodType: PropTypes.bool.isRequired,
  supplierFoodType: PropTypes.instanceOf(Immutable.List).isRequired,
  supplierFoodTypeList: PropTypes.arrayOf(PropTypes.shape({})),
  dispatchChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

ItinerarySupplierFoodForm.defaultProps = {
  itinerarySupplierFoodServices: Immutable.List(),
  itinerarySupplierFoodServiceDetail: Immutable.Map(),
  supplierFoodTypeList: [],
  loading: false,
};

const mapDispatchToProps = {
  dispatchGetItinerarySupplierFoodAssignmentService:
    getItinerarySupplierFoodAssignmentService,
  dispatchClearItinerarySupplierFoodService: clearItinerarySupplierFoodService,
  dispatchChange: change,
  dispatchGetItinerarySupplierFoodAssignmentServiceDetail:
    getItinerarySupplierFoodAssignmentServiceDetail,
  dispatchClearItinerarySupplierFoodServiceDetail:
    clearItinerarySupplierFoodServiceDetail,
  dispatchClearSupplierFoodType: clearSupplierFoodType,
  dispatchGetSupplierFoodTypeBySupplierAndItinerary:
    getSupplierFoodTypeBySupplierAndItinerary,
};

const selector = formValueSelector(FORM_NAME);

const mapStateToProps = ({ FoodUnit, form }) => {
  const assignmentService =
    FoodUnit.ItinerarySupplierFood.get('assignmentService');

  return {
    itinerarySupplierFoodServices: assignmentService.getIn(
      ['content'],
      Immutable.List(),
    ),
    loadingItinerarySupplierFoodServices: assignmentService.getIn(['loading']),
    itinerarySupplierFoodServiceDetail: assignmentService.getIn([
      'current',
      'content',
    ]),
    loadingItinerarySupplierFoodServiceDetail: !assignmentService
      .getIn(['current', 'activity'])
      .isEmpty(),
    loadingSupplierFoodType: !FoodUnit.Supplier.getIn([
      'foodType',
      'activity',
    ]).isEmpty(),
    supplierFoodType: FoodUnit.Supplier.getIn(
      ['foodType', 'content'],
      Immutable.List(),
    ),
    supplierFoodTypeList: selector({ form }, 'supplierFoodTypeList') || [],
    loading: !FoodUnit.ItinerarySupplierFood.getIn([
      'current',
      'activity',
    ]).isEmpty(),
  };
};

const formComponent = reduxForm({
  form: FORM_NAME,
})(ItinerarySupplierFoodForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
